/** A phrase's words as written, and as compared: case and punctuation dropped. */
function wordsOf(text: string) {
  const raw = text.split(/\s+/).filter(Boolean)
  return { raw, normalized: raw.map((word) => word.toLowerCase().replace(/[^\p{L}\p{N}']/gu, '')) }
}

/**
 * `next` without the words it repeats from the end of `previous`. A provider's
 * new utterance often re-sends the tail of the last one, and the live line can
 * overlap the final it follows; case and punctuation are ignored in the match.
 */
export function trimRepeatedWords(previous: string, next: string): string {
  const after = wordsOf(next)
  // Only as many trailing words as `next` has can overlap, so a long turn isn't re-read on every tick
  const before = wordsOf(
    previous
      .split(/\s+/)
      .slice(-after.raw.length - 1)
      .join(' '),
  ).normalized
  for (let length = Math.min(before.length, after.raw.length); length > 0; length--) {
    const tail = before.slice(before.length - length)
    if (tail.every((word, index) => word === after.normalized[index])) return after.raw.slice(length).join(' ')
  }
  return next
}

/**
 * `previous` followed by `next`, with what `next` re-sends from it said once.
 * A provider's new utterance can re-send the tail of the last one, or re-send
 * it from mid-phrase with a word revised ("misconduct. No. He" → "misconduct.
 * Oh. He"). `next` re-sends from the earliest point where it starts with the
 * same word and then lines up with `previous` but for one word in four.
 *
 * The result never drops words from `previous`: a re-send replaces the rest of
 * it only once it's at least as long, and until then `previous` shows as is.
 * Two phrases that merely share an opener ("I think…") line up for a word or
 * two and then part, so they join instead.
 */
export function joinWithoutRepeats(previous: string, next: string): string {
  const before = wordsOf(previous)
  const after = wordsOf(next)
  for (let start = 0; start < before.raw.length; start++) {
    if (after.normalized[0] !== before.normalized[start]) continue
    const remaining = before.raw.length - start
    const compared = Math.min(remaining, after.raw.length)
    const misses = after.normalized
      .slice(0, compared)
      .filter((word, index) => word !== before.normalized[start + index]).length
    if (misses > Math.floor(compared / 4)) continue
    return after.raw.length >= remaining ? [...before.raw.slice(0, start), ...after.raw].join(' ') : previous
  }
  return `${previous} ${next}`
}
